import React, { useState, useEffect, useCallback } from 'react';
import { ImageCard } from './ImageCard';
import {
  listCategoryImages,
  uploadPortfolioImage,
  updatePortfolioImage,
  deletePortfolioImage,
  setProjectCover,
  renameProject,
  persistImageOrder,
} from '../../lib/portfolio';

const NEW_PROJECT = '__new__';

// Gruppera platta rader till projekt i stabil ordning.
const groupProjects = (rows) => {
  const order = [];
  const map = new Map();
  for (const img of rows) {
    const key = img.project || 'Övrigt';
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key).push(img);
  }
  return order.map((name) => ({ name, images: map.get(name) }));
};

const ProjectBlock = ({ project, onReload, onSetCover, onDelete }) => {
  const [name, setName] = useState(project.name);
  const [saving, setSaving] = useState(false);
  const [imgs, setImgs] = useState(project.images);

  useEffect(() => {
    setImgs(project.images);
  }, [project.images]);

  const move = async (image, dir) => {
    const idx = imgs.findIndex((i) => i.id === image.id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= imgs.length) return;
    const next = [...imgs];
    [next[idx], next[j]] = [next[j], next[idx]];
    setImgs(next);
    await persistImageOrder(next);
  };

  const rename = async () => {
    if (!name.trim() || name === project.name) return;
    setSaving(true);
    try {
      await renameProject(project.images[0].category, project.name, name.trim());
      await onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-cv-panel border border-cv-border flex flex-col gap-4" style={{ borderRadius: 'var(--cv-radius)', padding: 20 }}>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="px-3 py-2 bg-cv-base border border-cv-edge rounded-cv text-cv-ink font-serif text-lg outline-none focus:border-cv-accent flex-1 min-w-[200px]"
        />
        {name.trim() && name !== project.name && (
          <button type="button" onClick={rename} disabled={saving} className="bg-cv-ink text-cv-base border-none px-4 py-2 text-xs tracking-[0.12em] uppercase rounded-cv cursor-pointer hover:bg-cv-ink2 transition-colors disabled:opacity-60">
            Byt namn
          </button>
        )}
        <span className="text-sm text-cv-faint">{project.images.length} bilder</span>
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))' }}>
        {imgs.map((img, i) => (
          <ImageCard
            key={img.id}
            image={img}
            showCover
            onSave={updatePortfolioImage}
            onSetCover={onSetCover}
            onDelete={onDelete}
            onMoveUp={(im) => move(im, -1)}
            onMoveDown={(im) => move(im, 1)}
            canMoveUp={i > 0}
            canMoveDown={i < imgs.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export const CategoryManager = ({ category, label }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');

  // Uppladdning
  const [projectChoice, setProjectChoice] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setImages(await listCategoryImages(category));
    } catch (err) {
      setStatus('Kunde inte hämta bilder: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    load();
  }, [load]);

  const projects = groupProjects(images);
  const projectNames = projects.map((p) => p.name);

  const upload = async (e) => {
    e.preventDefault();
    const projectName = projectChoice === NEW_PROJECT ? newProjectName.trim() : projectChoice;
    if (!files.length || !projectName) {
      setStatus('Välj projekt och minst en bild.');
      return;
    }
    setUploading(true);
    setStatus('');
    const isNewProject = !projectNames.includes(projectName);
    let ok = 0;
    try {
      for (let i = 0; i < files.length; i++) {
        await uploadPortfolioImage(files[i], {
          category,
          project: projectName,
          // Första bilden i ett helt nytt projekt blir omslag.
          isCover: isNewProject && i === 0,
        });
        ok += 1;
      }
      setStatus(`✓ ${ok} bild${ok === 1 ? '' : 'er'} tillagd${ok === 1 ? '' : 'a'} i "${projectName}"`);
      setFiles([]);
      setNewProjectName('');
      e.target.reset();
      await load();
    } catch (err) {
      setStatus(`Något gick fel efter ${ok} bild(er): ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const setCover = async (image) => {
    await setProjectCover(image);
    await load();
  };

  const remove = async (image) => {
    if (!window.confirm('Ta bort denna bild permanent?')) return;
    await deletePortfolioImage(image);
    await load();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-cv-panel border border-cv-border" style={{ borderRadius: 'var(--cv-radius)', padding: 20 }}>
        <p className="m-0 text-sm text-cv-muted leading-[1.7]">
          Här hanterar du bilderna för <strong>{label}</strong>. Bilderna grupperas i <strong>projekt</strong> (t.ex. ett
          enskilt möbelprojekt eller bröllop). Välj ett omslag som visas i rutnätet, döp om projekt, lägg till eller ta bort bilder.
        </p>
      </div>

      <form onSubmit={upload} className="bg-cv-base border border-cv-border flex flex-col gap-4" style={{ borderRadius: 'var(--cv-radius)', padding: 24 }}>
        <h3 className="m-0 font-serif font-medium text-[20px] text-cv-ink">Lägg till bilder</h3>
        <label className="flex flex-col gap-1.5 text-sm text-cv-muted">
          Till vilket projekt?
          <select value={projectChoice} onChange={(e) => setProjectChoice(e.target.value)} className="px-3 py-2.5 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent">
            <option value="">Välj projekt…</option>
            {projectNames.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
            <option value={NEW_PROJECT}>+ Nytt projekt…</option>
          </select>
        </label>
        {projectChoice === NEW_PROJECT && (
          <input type="text" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} placeholder="Namn på nytt projekt" className="px-3 py-2.5 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent" />
        )}
        <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files))} className="text-sm text-cv-muted" />
        <button type="submit" disabled={uploading || !files.length} className="w-fit bg-cv-ink text-cv-base border-none px-6 py-3 text-[13px] tracking-[0.14em] uppercase rounded-cv cursor-pointer hover:bg-cv-ink2 transition-colors disabled:opacity-60">
          {uploading ? 'Laddar upp…' : `Lägg till${files.length ? ` (${files.length})` : ''}`}
        </button>
        {status && <p className="m-0 text-sm text-cv-ink2">{status}</p>}
      </form>

      {loading ? (
        <p className="text-cv-muted">Laddar bilder…</p>
      ) : projects.length === 0 ? (
        <p className="text-cv-muted text-sm leading-[1.7]">
          Inga bilder i databasen ännu för den här tjänsten. Kör seed-scriptet (se SETUP-SUPABASE.md) så dyker alla nuvarande bilder upp här, eller lägg till egna ovan.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {projects.map((p) => (
            <ProjectBlock key={p.name} project={p} onReload={load} onSetCover={setCover} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
};
